import { ref } from "vue";
import type { CustomSignatureStatus } from "@/types/signature";
import { signatureStore } from "./useSignatureStore";

interface SignatureMethods {
  listen(
    signature: string,
    listener: (status: CustomSignatureStatus) => void,
  ): void;
  unlisten(signature: string): void;
}

export function useSignatureTracking(signatureMethods: SignatureMethods) {
  // data
  const listenedTransactions = ref<string[]>([]);
  const status = ref<Record<string, CustomSignatureStatus>>({});
  const createDefaultStatus = (): CustomSignatureStatus => ({
    confirmations: null,
    timestamp: Date.now() / 1000,
    status: "pending",
    err: null,
    slot: 0,
  });

  // methods
  function listen(
    sig: string,
    _unlisten: (sig: string, status: CustomSignatureStatus) => void = () => {},
  ): void {
    // Prevent duplicate listeners
    if (isTracking(sig)) {
      return;
    }

    // Add to tracked signatures
    listenedTransactions.value.push(sig);
    status.value[sig] = createDefaultStatus();

    // Setup listener with callback
    signatureMethods.listen(sig, (newStatus: CustomSignatureStatus) => {
      status.value[sig] = newStatus;

      // Auto-cleanup for terminal states (must match useSignatureStore stopListen conditions)
      if (
        newStatus.status === "timeout" ||
        newStatus.status === "error" ||
        newStatus.status === "confirmed" ||
        newStatus.status === "finalized" 
      ) {
        _unlisten(sig, status.value[sig]);
        unlisten(sig);
      }
    });
  }

  function unlisten(sig: string): void {
    signatureMethods.unlisten(sig);
    delete status.value[sig];
    const idx = listenedTransactions.value.indexOf(sig);
    if (idx !== -1) listenedTransactions.value.splice(idx, 1);
  }

  function unlistenAll(): void {
    for (const sig of listenedTransactions.value.slice()) {
      unlisten(sig);
    }
  }

  function clear(): void {
    status.value = {};
    unlistenAll();
  }

  function isTracking(sig: string): boolean {
    return listenedTransactions.value.includes(sig);
  }

  return {
    status,
    listenedTransactions,

    listen,
    unlisten,
    unlistenAll,
    clear,
    isTracking,
  };
}

export const signatureTracking = useSignatureTracking(signatureStore);
